import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { resolveHover, sameHover } from '../lib/view-sync';

/**
 * ViewSyncContext — transient cross-view state, sibling of SelectionContext.
 *
 *   hovered      { source: 'map'|'section'|'3d', modelId, x, depth, lonLat } | null
 *                (x = section x coordinate, depth = metres below surface). A view draws
 *                the hover only when `hovered.source` is not itself. The SectionView owner
 *                can read it with useViewSync().hovered and draw a cursor.
 *   sectionLine  { coordinates:[[lon,lat],..], modelId, name, xRange } | null
 *   syncCamera   boolean toggle; camera { lonLat, source, at } is the shared view centre
 *
 * setHover / publishCamera are throttled to one update per animation frame, and a pending
 * frame is cancelled on unmount.
 */
const noop = () => {};
const ViewSyncContext = createContext({
  hovered: null, setHover: noop, clearHover: noop,
  sectionLine: null, setSectionLine: noop,
  syncCamera: false, setSyncCamera: noop,
  camera: null, publishCamera: noop,
});

const raf = (fn) => (typeof requestAnimationFrame === 'function' ? requestAnimationFrame(fn) : setTimeout(fn, 16));
const caf = (id) => (typeof cancelAnimationFrame === 'function' ? cancelAnimationFrame(id) : clearTimeout(id));

export function ViewSyncProvider({ children }) {
  const [hovered, setHovered] = useState(null);
  const [sectionLine, setSectionLineState] = useState(null);
  const [syncCamera, setSyncCamera] = useState(false);
  const [camera, setCamera] = useState(null);

  const mounted = useRef(true);
  const frame = useRef(null);
  const pendingHover = useRef(undefined); // undefined = nothing pending, null = clear
  const pendingCamera = useRef(null);
  const sectionLineRef = useRef(null);
  sectionLineRef.current = sectionLine;

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; if (frame.current != null) caf(frame.current); };
  }, []);

  const flush = useCallback(() => {
    frame.current = null;
    if (!mounted.current) return;
    if (pendingHover.current !== undefined) {
      const next = pendingHover.current === null ? null : resolveHover(pendingHover.current, sectionLineRef.current);
      pendingHover.current = undefined;
      setHovered((prev) => (sameHover(prev, next) ? prev : next));
    }
    if (pendingCamera.current) {
      const c = pendingCamera.current;
      pendingCamera.current = null;
      setCamera({ ...c, at: Date.now() });
    }
  }, []);
  const schedule = useCallback(() => { if (frame.current == null) frame.current = raf(flush); }, [flush]);

  const setHover = useCallback((h) => { pendingHover.current = h || null; schedule(); }, [schedule]);
  const clearHover = useCallback(() => { pendingHover.current = null; schedule(); }, [schedule]);
  const publishCamera = useCallback((c) => { pendingCamera.current = c; schedule(); }, [schedule]);
  const setSectionLine = useCallback((sl) => {
    setSectionLineState((prev) => (JSON.stringify(prev) === JSON.stringify(sl || null) ? prev : (sl || null)));
  }, []);

  const value = useMemo(() => ({
    hovered, setHover, clearHover, sectionLine, setSectionLine, syncCamera, setSyncCamera, camera, publishCamera,
  }), [hovered, setHover, clearHover, sectionLine, setSectionLine, syncCamera, camera, publishCamera]);

  return <ViewSyncContext.Provider value={value}>{children}</ViewSyncContext.Provider>;
}

export function useViewSync() {
  return useContext(ViewSyncContext);
}

export default ViewSyncContext;
